import { ANALYTICS_EVENTS, trackEvent } from '@/features/analytics';
import { getOS } from '@/shared/lib/native-browser';
import { LoadingSpinner } from '@/shared/components/loading-spinner';
import { LogoMarquee } from '@/features/payments/components/logo-marquee';
import { usePostAuthCompletion } from '@/features/claims';
import { useEffect } from 'react';
import checkmark from '@/features/auth/assets/checkmark.webp';
import appleLogo from '@/features/auth/assets/logo-apple.svg';
import googleLogo from '@/features/auth/assets/logo-google.svg';
import { OfferStepperHeader } from './offer-stepper-header';

interface RegisterStepProps {
  offerLabels: string[];
  currentIndex: number;
  onComplete: () => void;
}

export function RegisterStep({ offerLabels, currentIndex, onComplete }: RegisterStepProps) {
  const {
    authStatus,
    handleApple,
    handleGoogle,
    running,
    error,
    retry,
    resign,
    relogin,
    hydrated,
  } = usePostAuthCompletion({ requirePurchase: true, onComplete });

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.SETTLEMENT_REGISTER_SHOWN);
    trackEvent(ANALYTICS_EVENTS.SIGN_IN_VIEWED);
  }, []);

  const isAndroid = getOS() === 'android';
  const busy = !hydrated || authStatus === 'loading' || running || (authStatus === 'authenticated' && !error);

  return (
    <div className="min-h-[100dvh] w-full bg-mf-neutral-50 font-body text-mf-neutral-900">
      <div className="mx-auto flex min-h-[100dvh] max-w-app flex-col bg-mf-neutral-50">
        <OfferStepperHeader offerLabels={offerLabels} currentIndex={currentIndex} />

        {busy ? (
          <div className="flex flex-1 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <LogoMarquee fade className="h-[196px] w-full shrink-0" />
            <div className="flex w-full flex-col items-center gap-4 px-4">
              <img src={checkmark} alt="" className="size-[112px] object-contain" />

              {error ? (
                <div className="flex w-full flex-col items-center gap-4">
                  <div className="flex w-full flex-col gap-2 text-center">
                    <h1 className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
                      We couldn&apos;t finish setup
                    </h1>
                    <p className="font-body text-base font-normal leading-5 text-mf-neutral-600">{error.message}</p>
                  </div>
                  <button
                    type="button"
                    onClick={
                      error.action === 'resign'
                        ? resign
                        : error.action === 'relogin'
                          ? () => void relogin()
                          : retry
                    }
                    className="flex h-[52px] w-full items-center justify-center rounded-full bg-mf-neutral-900 px-5 font-body text-base font-bold leading-5 text-white transition-opacity hover:opacity-90"
                  >
                    {error.action === 'resign'
                      ? 'Sign again'
                      : error.action === 'relogin'
                        ? 'Sign in again'
                        : 'Retry'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex w-full flex-col gap-2 text-center">
                    <h1 className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
                      You&apos;re All Set!
                    </h1>
                    <p className="font-body text-base font-normal leading-5 text-mf-neutral-600">
                      Create your account to track your claim and see what else you&apos;re owed
                    </p>
                  </div>

                  <div className="flex w-full flex-col gap-2">
                    {!isAndroid && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            trackEvent(ANALYTICS_EVENTS.SETTLEMENT_REGISTER_BUTTON_CLICK, {
                              metadata: { method: 'apple' },
                            });
                            handleApple();
                          }}
                          className="flex h-[52px] w-full items-center justify-center gap-1 rounded-full bg-mf-neutral-900 px-5 font-body text-base font-bold leading-5 text-white transition-opacity hover:opacity-90"
                        >
                          <img src={appleLogo} alt="" className="size-8" />
                          Sign in with Apple
                        </button>
                        <div className="flex items-center gap-2">
                          <span className="h-px flex-1 bg-mf-neutral-200" />
                          <span className="font-body text-sm leading-4 text-mf-neutral-600">or</span>
                          <span className="h-px flex-1 bg-mf-neutral-200" />
                        </div>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        trackEvent(ANALYTICS_EVENTS.SETTLEMENT_REGISTER_BUTTON_CLICK, {
                          metadata: { method: 'google' },
                        });
                        handleGoogle();
                      }}
                      className="flex h-[52px] w-full items-center justify-center gap-1 rounded-full bg-white px-5 font-body text-base font-bold leading-5 text-mf-neutral-900 shadow-[0_0_8px_rgba(26,22,19,0.03)] transition-opacity hover:opacity-90"
                    >
                      <img src={googleLogo} alt="" className="size-8" />
                      Sign in with Google
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
